import { lazy, Suspense, useEffect, useState } from "react";
import { Search } from "lucide-react";
import type { Row, Snapshot } from "../../api";
import "./search.css";

/** A page from the menu (nav/menu.ts searchablePages), found by its name. */
export type SearchPage = { id: string; label: string; group: string };

const SearchPalette = lazy(() => import("./SearchPalette").then((m) => ({ default: m.SearchPalette })));

/**
 * Global search: a box in the top bar and a keyboard palette (Cmd/Ctrl+K).
 * Results come grouped from GET /api/search, already scoped and redacted by
 * the server, after the pages in this person's menu whose name matches
 * (matched in the palette, no request); picking one opens the record itself (product editor, invoice,
 * count, recipe, supplier, member, POS item) on the page that holds it. The
 * palette's code loads the first time it opens.
 */
export function GlobalSearch({ s, go, pages = [] }: { s: Snapshot; go: (page: string) => void; pages?: SearchPage[] }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  return (
    <>
      <button className="gsearch-trigger" onClick={() => setOpen(true)} aria-label="Search everything" title="Search pages, products, recipes, suppliers, invoices, POS items, counts and people">
        <Search size={15} />
        <span>Search</span>
        <kbd>{isMac ? "⌘" : "Ctrl"} K</kbd>
      </button>
      {open && (
        <Suspense fallback={null}>
          <SearchPalette s={s} go={go} pages={pages} q={q} setQ={setQ} onClose={() => setOpen(false)} />
        </Suspense>
      )}
    </>
  );
}
export type { Row as SearchRow };
